import { FeedbackEntry, ProjectDetail, ProjectSubmission, ProjectSummary } from "@/types/project";

export type AuthUser = {
  id: string;
  username: string;
  email: string;
  created_at: string;
};

export type AuthResponse = {
  access_token: string;
  token_type: "bearer";
  user: AuthUser;
};

export type ApiValidationIssue = {
  type: string;
  loc: (string | number)[];
  msg: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
};

export class ApiError extends Error {
  status: number;
  detail?: ApiValidationIssue[] | string;

  constructor(message: string, status: number, detail?: ApiValidationIssue[] | string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

export function getApiBase(): string {
  if (typeof window === "undefined") {
    return process.env.INTERNAL_API_BASE ?? "http://backend:8000/api";
  }

  return process.env.NEXT_PUBLIC_API_BASE ?? "/api";
}

function buildApiErrorMessage(status: number, detail?: ApiValidationIssue[] | string): string {
  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail[0]?.msg ?? `Request failed with status ${status}`;
  }

  return `Request failed with status ${status}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${getApiBase()}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
    cache: "no-store",
  });

  if (!response.ok) {
    let detail: ApiValidationIssue[] | string | undefined;
    try {
      const payload = (await response.json()) as { detail?: ApiValidationIssue[] | string };
      detail = payload.detail;
    } catch {
      detail = undefined;
    }

    throw new ApiError(buildApiErrorMessage(response.status, detail), response.status, detail);
  }

  return response.json() as Promise<T>;
}

export async function registerAccount(payload: {
  username: string;
  email: string;
  password: string;
}): Promise<AuthResponse> {
  return request<AuthResponse>("/auth/register", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function loginAccount(payload: {
  identifier: string;
  password: string;
}): Promise<AuthResponse> {
  return request<AuthResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getCurrentUser(token: string): Promise<AuthUser> {
  return request<AuthUser>("/auth/me", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getNewApps(query?: string): Promise<ProjectSummary[]> {
  const queryString = query ? `?query=${encodeURIComponent(query)}` : "";
  return request<ProjectSummary[]>(`/projects/new${queryString}`);
}

export async function getBestApps(query?: string): Promise<ProjectSummary[]> {
  const queryString = query ? `?query=${encodeURIComponent(query)}` : "";
  return request<ProjectSummary[]>(`/projects/best${queryString}`);
}

export async function getProject(projectId: string): Promise<ProjectDetail> {
  return request<ProjectDetail>(`/projects/${projectId}`);
}

export async function submitProject(payload: ProjectSubmission): Promise<ProjectDetail> {
  return request<ProjectDetail>("/submissions/projects", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getFeedback(projectId: string): Promise<FeedbackEntry[]> {
  return request<FeedbackEntry[]>(`/projects/${projectId}/feedback`);
}

export async function addFeedback(
  projectId: string,
  payload: Omit<FeedbackEntry, "id" | "project_id" | "created_at">
): Promise<FeedbackEntry> {
  return request<FeedbackEntry>(`/projects/${projectId}/feedback`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
